import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Card, TextField, IconButton,
  Avatar, Chip, CircularProgress, Divider,
} from '@mui/material';
import { Send, AutoAwesome, Person, Delete, ContentCopy } from '@mui/icons-material';

const SYSTEM_PROMPT = `You are NOVA, an expert AI assistant for the Space Weather Monitoring System. 
You specialize in:
- Solar flares, geomagnetic storms, coronal mass ejections (CMEs)
- Space weather impacts on satellites, power grids, GPS, radio communications
- Aurora forecasts and visibility
- NASA missions and space science
- Planetary science and solar system facts
- Earth's magnetosphere and ionosphere
- Space weather alerts and safety advice

Always be helpful, accurate, and engaging. Use space-themed language naturally.
When asked about current conditions, remind users to check the Dashboard for live data.
Format responses clearly with bullet points when listing multiple items.`;

const SUGGESTED_QUESTIONS = [
  "What is a solar flare?",
  "How do geomagnetic storms affect Earth?",
  "What causes the Northern Lights?",
  "Is it safe to fly during a solar storm?",
  "What is the Kp index?",
  "How do CMEs differ from solar flares?",
  "What are X-class solar flares?",
  "How does space weather affect GPS?",
];

export default function AIChatPage() {
  const [messages, setMessages]   = useState([
    {
      role: 'assistant',
      content: "Hello! I'm **NOVA**, your AI space weather assistant 🌟\n\nI can help you understand solar flares, geomagnetic storms, CMEs, and all things space weather. I can also explain how space weather affects our daily technology and life on Earth.\n\nWhat would you like to know?",
      timestamp: new Date(),
    }
  ]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const messagesEndRef             = useRef(null);
  const inputRef                   = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;

    setInput('');
    setError('');

    const userMessage = { role: 'user', content: userText, timestamp: new Date() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setLoading(true);

  try {
  const token = localStorage.getItem('token');
 const backendUrl = process.env.REACT_APP_API_URL || '/api';
const response = await fetch(`${backendUrl}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      system: SYSTEM_PROMPT,
      messages: updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content })),
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `HTTP error ${response.status}`);
  }

  if (data.error) {
    throw new Error(data.error);
  }

  // Handle Anthropic response format
  let assistantText = '';
  if (data.content && Array.isArray(data.content)) {
    assistantText = data.content
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('\n');
  }

  if (!assistantText) {
    throw new Error('Empty response from AI');
  }

  setMessages(prev => {
    const next = [...prev, { role: 'assistant', content: assistantText, timestamp: new Date() }];
    return next.length > 100 ? next.slice(next.length - 100) : next;
  });

} catch (err) {
  setError(`Failed: ${err.message}`);
} finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! I'm NOVA, ready to answer your space weather questions 🚀",
      timestamp: new Date(),
    }]);
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(2,136,209,0.2), rgba(255,255,255,0.1))',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            <AutoAwesome sx={{ color: '#ffffff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>NOVA AI Assistant</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Your intelligent space weather guide
            </Typography>
          </Box>
          <Chip label="AI Powered" size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light',
              border: '1px solid rgba(255,255,255,0.3)', fontWeight: 700 }} />
        </Box>
        <IconButton onClick={clearChat} sx={{ color: 'text.secondary' }} title="Clear chat">
          <Delete />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden' }}>
        {/* Chat window */}
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{
                display: 'flex',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                gap: 1.5, alignItems: 'flex-start',
              }}>
                {/* Avatar */}
                <Avatar sx={{
                  width: 36, height: 36, flexShrink: 0,
                  bgcolor: msg.role === 'user' ? 'primary.dark' : 'rgba(255,255,255,0.15)',
                  border: msg.role === 'assistant' ? '1px solid rgba(255,255,255,0.3)' : 'none',
                }}>
                  {msg.role === 'user'
                    ? <Person sx={{ fontSize: 18 }} />
                    : <AutoAwesome sx={{ fontSize: 18, color: '#ffffff' }} />
                  }
                </Avatar>

                {/* Bubble */}
                <Box sx={{ maxWidth: '75%' }}>
                  <Box sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: msg.role === 'user'
                      ? 'rgba(2,136,209,0.2)'
                      : 'rgba(255,255,255,0.04)',
                    border: msg.role === 'user'
                      ? '1px solid rgba(2,136,209,0.3)'
                      : '1px solid rgba(255,255,255,0.08)',
                    borderTopRightRadius: msg.role === 'user' ? 4 : 16,
                    borderTopLeftRadius:  msg.role === 'user' ? 16 : 4,
                    position: 'relative',
                    '&:hover .copy-btn': { opacity: 1 },
                  }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.primary', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                    <IconButton
                      className="copy-btn"
                      size="small"
                      onClick={() => copyMessage(msg.content)}
                      sx={{
                        position: 'absolute', top: 4, right: 4,
                        opacity: 0, transition: 'opacity 0.2s',
                        color: 'text.secondary', p: 0.4,
                      }}
                    >
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" sx={{
                    color: 'text.secondary', mt: 0.5, display: 'block',
                    textAlign: msg.role === 'user' ? 'right' : 'left', px: 0.5,
                  }}>
                    {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Loading indicator */}
            {loading && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)' }}>
                  <AutoAwesome sx={{ fontSize: 18, color: '#ffffff' }} />
                </Avatar>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    NOVA is thinking...
                  </Typography>
                </Box>
              </Box>
            )}

            {error && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(211,47,47,0.1)',
                border: '1px solid rgba(211,47,47,0.3)' }}>
                <Typography variant="body2" sx={{ color: '#ef5350' }}>{error}</Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* Input */}
          <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask NOVA anything about space weather..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: 'primary.main', color: '#fff',
                width: 42, height: 42, borderRadius: 2, flexShrink: 0,
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.2)', color: 'text.secondary' },
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Card>

        {/* Suggested questions sidebar */}
        <Box sx={{ width: 240, flexShrink: 0, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
            Suggested Questions
          </Typography>
          {SUGGESTED_QUESTIONS.map((q, i) => (
            <Card key={i} onClick={() => sendMessage(q)}
              sx={{
                p: 1.5, cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { border: '1px solid rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.2s',
              }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                💬 {q}
              </Typography>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}